import React, { useState, useCallback, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import styles from './NewCarNavModal.module.scss';
import Image from 'next/image';
import { getBrowseCardConfigs } from '@factories/browseCardFactory';
import Text from '../Text';
import Button from "../Button";
import assetLoader from '@utils/internalImageLoder';
import { generateFilterValue } from '@helpers/navigationHelpers';
import Link from 'next/link';
import { useGlobalLoader } from '@hooks/useGlobalLoader';

interface NewCarNavModalProps {
  position?: number;
  onHover: (isHovering: boolean) => void;
  onClose: () => void;
  modalRef?: any;
  filterData?: any
}

const NewCarNavModal = ({
  position,
  onHover,
  onClose,
  modalRef,
  filterData,
}: NewCarNavModalProps) => {
  const [showAllBrands, setShowAllBrands] = useState<boolean>(false);
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
  const browseCardConfigs = getBrowseCardConfigs(filterData);
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  const generateHref = useCallback((optionValue: string, filterType: string) => {
    if (optionValue === '') {
      return '#';
    }
    const formattedValue = generateFilterValue(filterType, optionValue);
    return `/collection/${formattedValue}+cars/`;
  }, []);

  const handleOptionClick = useCallback((href: string) => {
    if (href !== pathname) {
      showLoader();
    }
    onClose();
  }, [onClose, pathname, showLoader]);

  const getFormattedTitle = useCallback((type: string) => {
    const titleMap: Record<string, string> = {
      'brand': 'Brands',
      'bodyType': 'Body Type',
      'fuelType': 'Fuel Type',
      'transmission': 'Transmission',
      'seatingCapacity': 'Seating Capacity',
      'budget': 'Price Range'
    };
    return titleMap[type] || type;
  }, []);

  return (
    <div
      ref={modalRef}
      className={styles.newCarNavModal}
      style={{ left: position ? `${position + 8}px` : '0' }}
      onMouseEnter={() => {
        setIsMouseOver(true);
        onHover(true);
      }}
      onMouseLeave={() => {
        setIsMouseOver(false);
        onHover(false);
      }}
    >
      <div className={styles.newCarNavInnerModal}>
        {
          browseCardConfigs.map((config, index) => (
            <React.Fragment key={index}>
              {
                config.type === "brand" ? (
                  <div className={styles.NavCard}>
                    <Text className={styles.NavCardTitle}>Cars by {getFormattedTitle(config.type)}</Text>
                    <div className={styles.options}>
                      {(showAllBrands ? config.options : config.options.slice(0, 12)).map((option, idx) => (
                        <Link
                          href={generateHref(option.value, config.type)}
                          key={idx}
                          className={styles.optionItem}
                          onClick={() => handleOptionClick(generateHref(option.value, config.type))}
                        >
                          <div className={styles.optionItemImageContainer}>
                            <Image
                              src={`${assetLoader()}/icons/car-brands-light/${option.label}.svg`}
                              alt={option.label}
                              width={28}
                              height={28}
                              loading='lazy'
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `${assetLoader()}/icons/fallbackCarLogo.svg`;
                              }}
                            />
                          </div>
                          <Text className={styles.optionItemText}>{option.label}</Text>
                        </Link>
                      ))}
                    </div>
                    {config.options.length > 12 && !showAllBrands && (
                      <Button
                        className={styles.NavItemViewMore}
                        onClick={() => setShowAllBrands(true)}
                      >View all brands
                        <Image
                          src={`${assetLoader()}/icons/chevron_down_light.svg`}
                          alt="view more"
                          width={20}
                          height={20}
                          loading='lazy'
                        />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className={styles.NavCard}>
                    <Text className={styles.NavCardTitle}>Cars by {getFormattedTitle(config.type)}</Text>
                    <div className={styles.restOptions}>
                      {config.options.map((option, idx) => (
                        <Link
                          href={generateHref(option.value, config.type)}
                          key={idx}
                          className={styles.restOptionItemText}
                          onClick={() => handleOptionClick(generateHref(option.value, config.type))}
                        >
                          {option.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              }
            </React.Fragment>
          ))
        }
      </div>
    </div>
  );
}

export default NewCarNavModal;